import API_BASE from '../../utils/api';
import React, { useState, useEffect, useRef, useCallback } from 'react';
import { useAuth } from '../../context/AuthContext';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import Sidebar from '../../components/Sidebar';
import ProfileDropdown from '../../components/ProfileDropdown';

const PrincipalStudents = () => {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const [students, setStudents] = useState([]);
  const [classes, setClasses] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [debouncedSearchTerm, setDebouncedSearchTerm] = useState('');
  const [filterClass, setFilterClass] = useState('');
  const [fetchError, setFetchError] = useState('');
  const latestRequestId = useRef(0);

  useEffect(() => {
    fetchClasses();
  }, []);

  // Debounce the search box so we don't fire a request on every keystroke
  useEffect(() => {
    const timer = setTimeout(() => setDebouncedSearchTerm(searchTerm), 300);
    return () => clearTimeout(timer);
  }, [searchTerm]);

  const fetchStudents = useCallback(async (requestId) => {
    try {
      const params = new URLSearchParams();
      if (filterClass) {
        const selectedClass = classes.find((c) => c._id === filterClass);
        if (selectedClass) {
          params.append('class', selectedClass.className);
          params.append('section', selectedClass.section);
        }
      }
      if (debouncedSearchTerm) params.append('search', debouncedSearchTerm);
      params.append('limit', '1000');
      params.append('includeAttendance', 'true');

      const response = await axios.get(`${API_BASE}/students?${params}`);
      // Ignore this response if a newer search/filter request has since been issued
      if (requestId !== latestRequestId.current) return;
      const data = response.data;
      setStudents(Array.isArray(data) ? data : data.students || []);
      setFetchError('');
    } catch (error) {
      if (requestId !== latestRequestId.current) return;
      console.error('Error fetching students:', error);
      setFetchError('Failed to load students. Please try again.');
    } finally {
      if (requestId === latestRequestId.current) setLoading(false);
    }
  }, [filterClass, classes, debouncedSearchTerm]);

  const fetchClasses = async () => {
    try {
      const response = await axios.get(`${API_BASE}/classes`);
      setClasses(response.data);
    } catch (error) {
      console.error('Error fetching classes:', error);
    }
  };

  const handleLogout = () => {
    logout();
    navigate('/login');
  };

  useEffect(() => {
    latestRequestId.current += 1;
    setLoading(true);
    fetchStudents(latestRequestId.current);
  }, [fetchStudents]);

  return (
    <div className="flex">
      <Sidebar />
      <div className="flex-1 bg-gray-100 min-h-screen">
        <nav className="bg-white shadow-sm sticky top-0 z-10">
  <div className="px-6 py-4 flex justify-between items-center">
    <h1 className="text-2xl font-semibold text-gray-800">Students</h1>
    <ProfileDropdown />
  </div>
</nav>

        <div className="p-6">
          {/* Filters */}
          <div className="bg-white rounded-lg shadow p-6 mb-6">
            <div className="flex gap-4">
              <input
                type="text"
                placeholder="Search students by name or roll number..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="flex-1 border rounded-md px-4 py-2"
              />
              <select
                value={filterClass}
                onChange={(e) => setFilterClass(e.target.value)}
                className="border rounded-md px-4 py-2"
              >
                <option value="">All Classes</option>
                {classes.map(cls => (
                  <option key={cls._id} value={cls._id}>
                    {cls.className} - {cls.section}
                  </option>
                ))}
              </select>
            </div>
          </div>

          {fetchError && (
            <div className="mb-6 p-4 rounded-lg bg-red-100 text-red-700 border border-red-400">
              {fetchError}
            </div>
          )}

          {/* Students Table */}
          <div className="bg-white rounded-lg shadow overflow-hidden">
            <table className="min-w-full divide-y divide-gray-200">
              <thead className="bg-gray-50">
                <tr>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Roll No</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Name</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Class</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Parent</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Attendance</th>
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-gray-200">
                {loading ? (
                  <tr>
                    <td colSpan="5" className="px-6 py-8 text-center text-gray-500">Loading...</td>
                  </tr>
                ) : students.length === 0 ? (
                  <tr>
                    <td colSpan="5" className="px-6 py-8 text-center text-gray-500">No students found</td>
                  </tr>
                ) : (
                  students.map((student) => (
                    <tr key={student._id} className="hover:bg-gray-50">
                      <td className="px-6 py-4 whitespace-nowrap">{student.rollNumber}</td>
                      <td className="px-6 py-4 whitespace-nowrap">{student.name}</td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        {student.classId?.className} - {student.classId?.section}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">{student.parentId?.name || 'Not Assigned'}</td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        {student.attendancePercentage == null ? (
                          <span className="px-2 py-1 bg-gray-100 text-gray-500 rounded-full text-xs">No data</span>
                        ) : (
                          <span className={`px-2 py-1 rounded-full text-xs ${student.attendancePercentage >= 75 ? 'bg-green-100 text-green-800' : student.attendancePercentage >= 50 ? 'bg-yellow-100 text-yellow-800' : 'bg-red-100 text-red-800'}`}>
                            {student.attendancePercentage}%
                          </span>
                        )}
                      </td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>
        </div>
      </div>
    </div>
  );
};

export default PrincipalStudents;